import React, { useEffect, useRef } from 'react';
import {
  ArrowRight,
  ScanFace,
  Camera,
  Users,
  ShieldCheck,
  Lock,
  Clock,
  FileSpreadsheet,
  AlertTriangle,
  MessageCircle,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { ROUTES } from 'app/javascript/constants/routes';
import Footer from 'app/javascript/components/shared/Footer';
import SEO from 'app/javascript/components/shared/SEO';
import { track } from 'app/javascript/utils/analytics';

interface StepCardProps {
  number: string;
  icon: React.ElementType;
  title: string;
  description: string;
  highlight?: boolean;
}

const StepCard: React.FC<StepCardProps> = ({ number, icon: Icon, title, description, highlight }) => (
  <div
    className={`relative rounded-2xl border p-6 md:p-8 transition-all duration-500 ${
      highlight
        ? 'bg-slate-800/60 border-yellow-400/40 shadow-xl shadow-yellow-400/5'
        : 'bg-slate-900/60 border-slate-700/40 hover:border-yellow-400/30'
    }`}
  >
    <div className="flex items-center justify-between mb-6">
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
          highlight ? 'bg-yellow-400 shadow-yellow-400/30' : 'bg-yellow-400 shadow-yellow-400/20'
        }`}
      >
        <Icon className="w-6 h-6 text-slate-900" strokeWidth={2.5} />
      </div>
      <span className="font-bebas text-5xl tracking-wide text-slate-700">{number}</span>
    </div>
    <h3 className="font-bebas text-2xl text-white mb-3 tracking-wide">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
  </div>
);

interface BenefitCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  index: number;
}

const BenefitCard: React.FC<BenefitCardProps> = ({ icon: Icon, title, description, index }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          card.classList.add('animate-card-in');
          observer.unobserve(card);
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="opacity-0 group rounded-2xl bg-slate-900/60 border border-slate-700/40 p-6 md:p-7 hover:border-yellow-400/30 transition-all duration-500"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="w-11 h-11 rounded-xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center mb-5 group-hover:bg-yellow-400/20 transition-all duration-300">
        <Icon className="w-5 h-5 text-yellow-400" strokeWidth={2.5} />
      </div>
      <h3 className="font-bebas text-xl md:text-2xl text-white mb-3 tracking-wide">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
    </div>
  );
};

const FAQ_ITEMS = [
  {
    question: 'Por que não dá pra marcar presença manualmente?',
    answer:
      'Por escolha. Toda presença no BlackBelts é comprovada por reconhecimento facial — sem chamada manual, sem listinha, sem "fia, marca minha presença". Isso elimina os dois maiores buracos da chamada de papel: marca de quem faltou e desencontro entre o que o professor lembra e o que o aluno alega. Se a foto não comprovou, a presença não cai. É uma feature, não uma limitação.',
  },
  {
    question: 'E se um aluno aparecer de costas ou tampado na foto?',
    answer:
      'O sistema só marca presença de quem reconhece com confiança. A solução é tirar outra foto com o aluno enquadrado. No plano grátis, você pode testar com até 20 fotos de controle de presença por reconhecimento facial; nos planos pagos, os envios são ilimitados. Sem reconhecimento na imagem, não há presença marcada.',
  },
  {
    question: 'E se um aluno sair no meio do treino?',
    answer:
      'O professor tira uma foto antes dele sair. Cada aula aceita várias fotos durante a sessão — todas entram no registro de presença daquela turma, respeitando o limite do plano grátis ou de forma ilimitada nos planos pagos. Quem não aparecer em nenhuma foto fica sem presença naquela aula.',
  },
  {
    question: 'E se o professor esquecer de tirar a foto?',
    answer:
      'A plataforma manda lembrete no celular do professor próximo ao horário previsto de fim da aula. Se mesmo assim a foto não for tirada, não há registro de presença daquela aula — porque presença sem foto é exatamente o que o BlackBelts existe pra evitar. Esquecer vira hábito de tirar.',
  },
  {
    question: 'E aluno novo que ainda não cadastrou?',
    answer:
      'Aluno só entra no histórico depois de cadastrado. A primeira coisa quando ele chega na sua academia é fazer a selfie no app — leva menos de 1 minuto. Da próxima foto em diante, ele já é reconhecido automaticamente.',
  },
  {
    question: 'Funciona com criança? E com gêmeos?',
    answer:
      'Funciona com criança a partir de 6 anos, com consentimento dos pais no cadastro. Gêmeos idênticos é o caso mais difícil pra qualquer sistema de reconhecimento facial — em casos raros de baixa confiança, o sistema sinaliza a dúvida no painel em vez de chutar.',
  },
  {
    question: 'Meus alunos vão precisar baixar app?',
    answer:
      'Só pra fazer o cadastro inicial (uma selfie). Depois nunca mais. O aluno só precisa estar no tatame quando a foto for tirada.',
  },
  {
    question: 'Isso é legal? E a LGPD?',
    answer:
      'Sim. Dado biométrico é categoria especial pela LGPD e tratamos com o nível de cuidado que ela exige: consentimento explícito no cadastro, criptografia em repouso e em trânsito, direito de exclusão a qualquer momento e log completo de acesso.',
  },
  {
    question: 'Quanto tempo leva pra implantar?',
    answer:
      'A academia fica pronta no mesmo dia. O cadastro dos alunos roda em paralelo — à medida que cada um chega pra treinar, faz a selfie e está dentro.',
  },
];

interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  const toggle = () => {
    if (!open) track('faq_open', { page: 'reconhecimento-facial', question });
    setOpen(!open);
  };
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-700/40 overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left hover:bg-slate-800/40 transition-colors"
        aria-expanded={open}
      >
        <span className="text-sm md:text-base font-semibold text-white">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180 text-yellow-400' : ''}`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 md:px-6 md:pb-6 text-sm text-slate-400 leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
};

export default function FaceRecognitionLandingPage() {
  // Same CTA contract as CompaniesLandingPage: the academy is created in the
  // app, so the click goes to the store.
  return (
    <main className="min-h-screen bg-slate-950 text-white overflow-hidden">
      <SEO
        title="Presença por Reconhecimento Facial no Jiu-Jitsu | BlackBelts"
        description="Controle de presença para academia de Jiu-Jitsu: o professor fotografa a turma no final do treino e o BlackBelts reconhece cada aluno automaticamente. Sem chamada, sem papel."
      />

      <div
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(90deg, #fbbf24 1px, transparent 1px),
            linear-gradient(180deg, #fbbf24 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-slate-950 via-transparent to-slate-950 pointer-events-none" />

      {/* ── Hero ── */}
      <section className="relative z-10 px-4 sm:px-6 pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-yellow-400/5 rounded-full blur-[140px] pointer-events-none" />

        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/30 mb-6 animate-fade-in">
            <Sparkles className="w-3.5 h-3.5 text-yellow-400" strokeWidth={2.5} />
            <span className="text-[11px] font-bold uppercase tracking-wider text-yellow-400">Novidade · Reconhecimento facial</span>
          </div>

          <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tight mb-6 animate-fade-in">
            <span className="text-white">UMA FOTO.</span>
            <br />
            <span className="text-white">TODA A </span>
            <span className="text-yellow-400">TURMA PRESENTE.</span>
          </h1>

          <p
            className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto mb-8 leading-relaxed animate-fade-in"
            style={{ animationDelay: '100ms' }}
          >
            No final do treino, o professor tira uma foto da turma. Em segundos, o BlackBelts reconhece cada aluno e marca a
            presença de todos — automaticamente. Sem chamada, sem papel, sem aluno fantasma.
          </p>

          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in mb-12 md:mb-16"
            style={{ animationDelay: '200ms' }}
          >
            <a
              href={ROUTES.DOWNLOAD}
              onClick={() => track('cta_click', { page: 'reconhecimento-facial', position: 'hero' })}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-yellow-400 text-slate-900 font-bebas text-lg uppercase tracking-wide shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 hover:shadow-yellow-400/30 transition-all duration-300"
            >
              Começar grátis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </a>
            <span className="text-sm text-slate-500">Sem cartão de crédito · pronto no mesmo dia</span>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div
            className="relative animate-fade-in"
            style={{ animationDelay: '300ms' }}
          >
            <div className="absolute -inset-8 md:-inset-12 bg-yellow-400/10 rounded-[3rem] blur-3xl -z-10" />
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden border border-slate-700/60 shadow-2xl shadow-black/50 bg-slate-900">
              <picture>
                <source srcSet="/face-recognition-team.webp" type="image/webp" />
                <img
                  src="/face-recognition-team.png"
                  alt="Foto de uma turma com 10 alunos identificados pelo reconhecimento facial do BlackBelts"
                  className="w-full h-auto block"
                  loading="eager"
                  fetchPriority="high"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pain ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-bebas text-3xl sm:text-4xl md:text-5xl text-white text-center mb-4">
            A LISTA DE PRESENÇA É O PONTO CEGO DA SUA ACADEMIA DE JIU-JITSU
          </h2>
          <p className="text-base md:text-lg text-slate-400 text-center max-w-2xl mx-auto mb-12">
            Você sabe quanto fatura, quanto paga de aluguel, quem está em dia. Mas se eu te perguntar quem treinou terça à
            noite, você abre um caderno — ou chuta.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: AlertTriangle, text: 'Aluno parou de aparecer há 3 semanas — você só descobre quando ele cancela' },
              { icon: Clock, text: 'Professor gasta 5 a 10 minutos chamando lista — todo dia, todas as turmas' },
              { icon: FileSpreadsheet, text: 'Lista no papel que some, rasura, ou que ninguém preencheu na correria da aula' },
              { icon: MessageCircle, text: 'Aluno fantasma que paga, não treina, e cancela sem nenhum sinal pra você' },
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-start gap-4 p-5 rounded-xl bg-red-500/5 border border-red-500/10 hover:border-red-500/20 transition-colors"
              >
                <Icon className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 text-sm leading-relaxed">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">TRÊS PASSOS. UM CLIQUE.</h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">Funciona no celular do professor. Sem hardware, sem instalação.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            <StepCard
              number="01"
              icon={ScanFace}
              title="O ALUNO SE CADASTRA UMA VEZ"
              description="No próprio celular, em menos de 1 minuto. Tira uma selfie no app, confirma, pronto. Não precisa repetir nunca mais."
            />
            <StepCard
              number="02"
              icon={Camera}
              title="O PROFESSOR TIRA UMA FOTO DA TURMA"
              description="No final da aula, todo mundo no tatame, uma foto. Direto do celular dele, dentro do BlackBelts."
              highlight
            />
            <StepCard
              number="03"
              icon={Users}
              title="A PRESENÇA MARCA SOZINHA"
              description="O sistema reconhece cada rosto e marca a presença de todos. Em segundos, vinculada à turma, ao horário, ao dia."
            />
          </div>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">O QUE MUDA NA SUA ACADEMIA</h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">A partir da próxima aula que entrar.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            <BenefitCard
              index={0}
              icon={Clock}
              title="ADEUS CHAMADA. OLÁ TATAME."
              description="O professor para de gastar 5 a 10 minutos por turma chamando lista. Uma foto no final cobre o que a chamada cobria — em 2 segundos. O resto vira aula."
            />
            <BenefitCard
              index={1}
              icon={ShieldCheck}
              title="PRESENÇA COMPROVADA. ALUNO FANTASMA EXPOSTO."
              description="Toda presença no histórico foi reconhecida por uma foto — ninguém preenche na mão, ninguém marca quem faltou. Você vê quem treinou de verdade e quem sumiu nos últimos 14 dias antes de virar boleto cancelado."
            />
            <BenefitCard
              index={2}
              icon={FileSpreadsheet}
              title="HISTÓRICO POR TURMA, HORÁRIO, DIA"
              description="Cada presença fica vinculada à aula em que aconteceu. Frequência média da turma das 6h da terça? Está lá. Qual professor segura mais aluno? Está lá."
            />
            <BenefitCard
              index={3}
              icon={Lock}
              title="PRIVACIDADE SÉRIA, LGPD EM DIA"
              description="Dados biométricos criptografados. Aluno consente no cadastro e pode excluir quando quiser. Política de retenção das fotos configurável e log de acesso completo."
            />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-bebas text-3xl sm:text-4xl md:text-5xl text-white mb-4">PERGUNTAS QUE TODO DONO FAZ</h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">As respostas honestas, antes da demo.</p>
          </div>

          <div className="space-y-3">
            {FAQ_ITEMS.map((item) => (
              <FAQItem key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative z-10 px-4 sm:px-6 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 sm:p-12 md:p-16 rounded-3xl bg-slate-800/50 border border-slate-700/50 overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-yellow-400/8 rounded-full blur-[100px]" />

            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-yellow-400 flex items-center justify-center mx-auto mb-6 shadow-xl shadow-yellow-400/20">
                <ScanFace className="w-8 h-8 text-slate-900" strokeWidth={2.5} />
              </div>

              <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">
                SUA PRÓXIMA AULA AINDA É NO PAPEL?
              </h2>
              <p className="text-base md:text-lg text-slate-400 mb-10 max-w-xl mx-auto">
                Cadastre sua academia agora. Os alunos fazem o cadastro à medida que chegam, e a partir da próxima aula a
                presença marca sozinha.
              </p>

              <a
                href={ROUTES.DOWNLOAD}
                onClick={() => track('cta_click', { page: 'reconhecimento-facial', position: 'final' })}
                className="group inline-flex items-center gap-3 px-10 py-5 rounded-xl bg-yellow-400 text-slate-900 font-bebas text-lg uppercase tracking-wide shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 hover:shadow-yellow-400/30 transition-all duration-300"
              >
                Começar grátis
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>

              <p className="text-xs text-slate-500 mt-6">Sem cartão de crédito · pronto no mesmo dia</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
